const ROOT = '/api/collections'
let collectionsUserId = ''

export function setCollectionsUser(userId) {
  collectionsUserId = String(userId || '').trim()
}

export function getCollectionsUser() { return collectionsUserId }

export function queryString(params = {}) {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  })
  const value = search.toString()
  return value ? `?${value}` : ''
}

export function endpoint(path, params) {
  return `${ROOT}/${String(path).replace(/^\/+/, '')}${queryString(params)}`
}

export async function request(path, { params, body, signal, ...options } = {}) {
  const isForm = typeof globalThis.FormData !== 'undefined' && body instanceof globalThis.FormData
  const headers = body === undefined || isForm
    ? { ...options.headers }
    : { 'content-type': 'application/json', ...options.headers }
  if (collectionsUserId && !Object.keys(headers).some((name) => name.toLowerCase() === 'x-user-id')) {
    headers['X-User-Id'] = collectionsUserId
  }
  const response = await fetch(endpoint(path, params), {
    ...options,
    signal,
    headers,
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    const detail = data?.detail
    const apiError = data?.error
    const message = (typeof detail === 'string' ? detail : detail?.message)
      || (typeof apiError === 'string' ? apiError : apiError?.message)
      || data?.message
      || `Collections API request failed (${response.status})`
    const error = new Error(message)
    error.status = response.status
    error.code = data?.code || detail?.code || apiError?.code || data?.result
      || (typeof apiError === 'string' && /^[a-z][a-z0-9_]+$/.test(apiError) ? apiError : null)
      || (typeof detail === 'string' && /^[a-z][a-z0-9_]+$/.test(detail) ? detail : null)
      || null
    error.data = data
    throw error
  }
  return data
}

export async function download(path, filename, params = {}) {
  const response = await fetch(endpoint(path, params))
  if (!response.ok) throw new Error(`Export failed (${response.status})`)
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export const money = (value, currency = 'ILS', compact = false) => {
  if (value === null || value === undefined || value === '') return 'Unavailable'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 'Unavailable'
  const supported = new Set(['ILS', 'USD', 'GBP', 'EUR', 'CAD'])
  const normalizedCurrency = supported.has(String(currency).toUpperCase())
    ? String(currency).toUpperCase()
    : 'ILS'
  return new Intl.NumberFormat('en-IL', {
    style: 'currency', currency: normalizedCurrency,
    maximumFractionDigits: compact ? 1 : 2,
    notation: compact ? 'compact' : 'standard',
  }).format(amount)
}

export const number = (value, digits = 0) =>
  new Intl.NumberFormat('en-IL', { maximumFractionDigits: digits }).format(Number(value || 0))

export const shortDate = (value) => {
  if (!value) return '—'
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`)
  return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('en-IL', { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

export const titleize = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase())

export function taskCount(groups) {
  return Object.values(groups || {}).reduce((total, rows) => total + (Array.isArray(rows) ? rows.length : 0), 0)
}

export function chartPoints(due = [], promised = []) {
  const promisedByDate = new Map(promised.map((point) => [point.date, Number(point.amount || 0)]))
  return due.map((point) => ({
    date: point.date,
    due: Number(point.amount || 0),
    promised: promisedByDate.get(point.date) || 0,
  }))
}

export function paymentReviewPayload(action, correction = '') {
  const allowed = new Set(['confirm', 'correct', 'reject', 'flag_for_query'])
  if (!allowed.has(action)) throw new Error(`Unknown payment review action: ${action}`)
  if (action !== 'correct') return { action }
  const invoiceIds = String(correction).split(',').map((value) => value.trim()).filter(Boolean)
  if (!invoiceIds.length) throw new Error('At least one invoice ID is required to correct a match')
  return { action, invoice_ids: invoiceIds }
}

export const PAYMENT_STATES = [
  'ingested', 'matching', 'review_required', 'auto_close_pending',
  'receipt_pending', 'matched', 'rejected', 'query_required',
  'match_failed', 'writeback_failed', 'reconciliation_required',
]

export function normalizePage(data, fallbackLimit = 25) {
  const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
  return {
    items,
    total: Number(data?.total ?? data?.count ?? items.length),
    offset: Number(data?.offset ?? 0),
    limit: Number(data?.limit ?? fallbackLimit),
    counts: data?.counts || data?.canonical_counts || data?.workflow_counts || {},
  }
}

export function normalizePayment(value = {}) {
  const source = value.payment || value.record || value
  const deposit = source.deposit || source.transaction || source
  const latestAttempt = value.latest_attempt || source.latest_attempt || source.match_attempts?.at?.(-1) || null
  const match = value.match || source.match || source.latest_result || latestAttempt?.result || null
  const receipt = value.receipt_outbox || source.receipt_outbox || source.receipt || null
  return {
    ...source,
    ...deposit,
    deposit_id: deposit.deposit_id || source.deposit_id || value.deposit_id,
    batch_id: source.batch_id || deposit.batch_id,
    workflow_state: source.workflow_state || value.workflow_state || 'ingested',
    version: Number(source.version ?? value.version ?? 1),
    latest_attempt: latestAttempt,
    match,
    review: value.review || source.review || null,
    task: value.task || source.task || null,
    receipt_outbox: receipt,
  }
}

export function normalizeReview(value = {}) {
  const review = value.review || value
  const payment = normalizePayment(value.payment || {
    ...review,
    workflow_state: review.payment_workflow_state || review.workflow_state,
    version: review.payment_version || review.version,
    receipt: review.receipt,
  })
  const deposit = review.deposit || payment
  const match = review.result || review.match || payment.match
  return {
    ...review,
    deposit,
    payment,
    match,
    deposit_id: review.deposit_id || deposit.deposit_id || payment.deposit_id,
    workflow_state: review.payment_workflow_state || review.workflow_state || payment.workflow_state,
    version: Number(review.version ?? payment.version ?? 1),
    task: value.task || review.task || payment.task || null,
    gate_outcomes: review.gate_outcomes || match?.gates || match?.gate_outcomes || [],
  }
}

export function operationKey(action, values, version = '') {
  const identity = (Array.isArray(values) ? values : [values]).filter(Boolean).map(String).sort().join(',')
  return `dashboard:${action}:${identity}${version === '' ? '' : `:v${version}`}`
}

export async function uploadOperationKey(file) {
  if (!file || typeof file.arrayBuffer !== 'function') throw new Error('The selected bank file cannot be read')
  const bytes = new Uint8Array(await file.arrayBuffer())
  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
    const checksum = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
    return operationKey('upload-sha256', checksum)
  }
  // Fallback for non-secure contexts (e.g. served over a LAN IP, not https/localhost),
  // where SubtleCrypto is unavailable: a content-based FNV-1a digest still yields a
  // stable idempotency key so re-uploading the same file dedupes.
  let hash = 0x811c9dc5
  for (let i = 0; i < bytes.length; i += 1) {
    hash ^= bytes[i]
    hash = Math.imul(hash, 0x01000193)
  }
  const checksum = `${(hash >>> 0).toString(16)}-${bytes.length}`
  return operationKey('upload-fnv1a', checksum)
}

export function matchRequest(depositIds, records = []) {
  const ids = (Array.isArray(depositIds) ? depositIds : [depositIds]).filter(Boolean).map(String).sort()
  const versions = ids.map((id) => records.find((item) => item.deposit_id === id)?.version || 1).join('-')
  const idempotencyKey = operationKey('match', ids, versions)
  return { idempotencyKey, body: { deposit_ids: ids, idempotency_key: idempotencyKey } }
}

export function batchMatchRequest(batchId, records = []) {
  const ready = records.filter((item) => item?.deposit_id).sort((left, right) => left.deposit_id.localeCompare(right.deposit_id))
  const identity = [batchId, ...ready.map((item) => item.deposit_id)]
  const versions = ready.map((item) => item.version || 1).join('-')
  const idempotencyKey = operationKey('match-batch', identity, versions)
  return { idempotencyKey, body: { idempotency_key: idempotencyKey } }
}

export function summarizeBulkMatch(result, requestedCount) {
  const items = Array.isArray(result?.items) ? result.items : []
  const counts = items.reduce((summary, item) => {
    const outcome = item?.outcome || 'unknown'
    summary[outcome] = (summary[outcome] || 0) + 1
    return summary
  }, {})
  const total = Number(result?.total ?? (items.length || requestedCount))
  const processed = Number(counts.processed || 0)
  const failures = Math.max(0, total - processed)
  const detail = Object.entries(counts).map(([outcome, count]) => `${count} ${titleize(outcome).toLowerCase()}`).join(', ')
  return {
    complete: total > 0 && failures === 0,
    text: failures
      ? `Matching completed with exceptions: ${detail || `${failures} failed`}. Review the affected payments before continuing.`
      : `Matching completed for ${processed || requestedCount} payment${(processed || requestedCount) === 1 ? '' : 's'}.`,
  }
}

// Chunked matching: each deposit is matched by its own HTTP request because a
// single request carrying N deposits runs N sequential ~23s model calls and
// exceeds the API Gateway 30s ceiling. Same number of model calls, framed as N
// requests instead of one.
export function singleMatchRequest(depositId, record = {}) {
  const id = String(depositId)
  const version = Number(record?.version) || 1
  const idempotencyKey = operationKey('match', [id], String(version))
  return { idempotencyKey, body: { idempotency_key: idempotencyKey, expected_version: version } }
}

export function summarizeChunkedMatch(results, requestedCount) {
  const items = Array.isArray(results) ? results : []
  const counts = items.reduce((summary, item) => {
    const outcome = item?.outcome || 'unknown'
    summary[outcome] = (summary[outcome] || 0) + 1
    return summary
  }, {})
  const processed = Number(counts.processed || 0)
  const total = items.length || Number(requestedCount) || 0
  const failures = Math.max(0, total - processed)
  const detail = Object.entries(counts)
    .map(([outcome, count]) => `${count} ${titleize(outcome).toLowerCase()}`)
    .join(', ')
  return {
    complete: total > 0 && failures === 0,
    text: failures
      ? `Matching completed with exceptions: ${detail || `${failures} failed`}. Review the affected payments before continuing.`
      : `Matching completed for ${processed} payment${processed === 1 ? '' : 's'}.`,
  }
}

export function reviewDecisionPayload(action, review, { actor = 'dashboard_user', note = '', allocation = {}, context = null, invoiceVersions = {} } = {}) {
  const allowed = new Set(['confirm', 'correct', 'reject', 'flag_for_query'])
  if (!allowed.has(action)) throw new Error(`Unknown payment review action: ${action}`)
  const depositId = review.deposit_id || review.deposit?.deposit_id
  const version = Number(review.version)
  if (!depositId || !Number.isFinite(version)) throw new Error('This review is missing a durable ID or version; refresh before acting')
  const body = { action, expected_version: version, actor, idempotency_key: operationKey(`review-${action}`, depositId, version) }
  if (note.trim()) body.note = note.trim()
  if (context && ['confirm', 'correct'].includes(action)) {
    body.context_version = context.context_version
    body.expected_payment_version = context.payment_version
    body.invoice_balance_versions = invoiceVersions
  }
  if (action === 'correct') {
    const entries = Object.entries(allocation).map(([invoiceId, amount]) => [invoiceId.trim(), Number(amount)])
      .filter(([invoiceId, amount]) => invoiceId && Number.isFinite(amount) && amount > 0)
    if (!entries.length) throw new Error('Add at least one invoice allocation greater than zero')
    const correctedAllocation = Object.fromEntries(entries)
    const total = entries.reduce((sum, [, amount]) => sum + amount, 0)
    const depositAmount = Number(review.deposit?.amount ?? review.payment?.amount)
    if (Number.isFinite(depositAmount) && total - depositAmount > 0.005) {
      throw new Error(`Allocation cannot exceed the deposit amount (${depositAmount})`)
    }
    body.invoice_ids = Object.keys(correctedAllocation)
    body.corrected_allocation = correctedAllocation
  }
  return body
}

export function paymentStatePresentation(state) {
  if (['matched'].includes(state)) return { label: 'Matched', tone: 'success', finalized: true }
  if (['rejected'].includes(state)) return { label: 'Rejected', tone: 'danger', finalized: true }
  if (['review_required', 'query_required', 'reconciliation_required'].includes(state)) return { label: titleize(state), tone: 'warning', finalized: false }
  if (['match_failed', 'writeback_failed'].includes(state)) return { label: titleize(state), tone: 'danger', finalized: false }
  if (['matching', 'auto_close_pending', 'receipt_pending'].includes(state)) return { label: titleize(state), tone: 'purple', finalized: false }
  return { label: titleize(state || 'ingested'), tone: 'neutral', finalized: false }
}

export function canRunMatch(payment) {
  return ['ingested', 'review_required', 'match_failed'].includes(payment?.workflow_state)
}

export function chatSources(data) {
  const list = Array.isArray(data?.sources) ? data.sources : Array.isArray(data?.citations) ? data.citations : []
  return list.map((source) => ({ id: source.id || source.label, label: source.label || source.title || source.id || String(source) }))
}
